import { StateNode } from "./canvas";
import { createShapeId, type TLShapeId, type TLShape } from "@tldraw/tlschema";
import type { DraftingShape, Point } from "../shared/schema";
import {
  nearestWallSegment,
  openingPlacement,
  type DraftingKind,
} from "../shared/drafting";
import { resolvedDraftingStyle } from "./drafting-defaults";
import { scaleSource, sourceMeters } from "./source-scale";
import { activeTrace } from "./tracing";
import { sheetAt } from "./sheet-operations";
import { notify } from "./api";
export class DraftingTool extends StateNode {
  static override id = "polyline";
  points: Point[] = [];
  shapeId?: TLShapeId;
  drawingParent?: TLShape;
  history?: string;
  kind() {
    return this.getPath().split(".").at(-1) as DraftingKind;
  }
  override onEnter() {
    this.editor.setCursor({ type: "cross", rotation: 0 });
    this.points = [];
  }
  point() {
    const p = this.editor.inputs.getCurrentPagePoint();
    const local = this.drawingParent
      ? this.editor.getPointInShapeSpace(this.drawingParent, p)
      : p;
    return { x: local.x, y: local.y };
  }
  override onPointerDown() {
    if (this.editor.getIsReadonly()) return;
    const kind = this.kind();
    if (kind === "door" || kind === "window") {
      this.placeOpening();
      return;
    }
    if (!this.points.length) {
      const pagePoint = this.editor.inputs.getCurrentPagePoint();
      this.drawingParent =
        activeTrace(this.editor) || sheetAt(this.editor, pagePoint);
      if (
        this.drawingParent &&
        (this.editor.isShapeOrAncestorLocked(this.drawingParent) ||
          this.editor.isShapeHidden(this.drawingParent))
      )
        this.drawingParent = undefined;
      this.history = this.editor.markHistoryStoppingPoint(`Draw ${kind}`, { hold: true });
    }
    const p = this.point();
    if (
      this.points.length &&
      Math.hypot(p.x - this.points.at(-1)!.x, p.y - this.points.at(-1)!.y) <
        0.01
    )
      return;
    if (this.points.length >= 999) {
      notify("Finish this path before adding more points.");
      return;
    }
    this.points.push({ x: p.x, y: p.y });
    if (!this.shapeId) {
      this.shapeId = createShapeId();
      this.editor.createShape<DraftingShape>({
        id: this.shapeId,
        type: "drafting",
        parentId: this.drawingParent?.id ?? this.editor.getCurrentPageId(),
        x: 0,
        y: 0,
        opacity: 1,
        props: {
          ...resolvedDraftingStyle(
            this.editor,
            kind,
            sourceMeters(
              this.editor,
              scaleSource(this.editor, this.drawingParent),
            ),
          ),
          points: [p, { x: p.x + 0.01, y: p.y + 0.01 }],
        },
      });
    }
    if (kind === "leader" && this.points.length === 2) this.finish();
    else if (kind === "curve" && this.points.length === 3) {
      this.points = [this.points[0], this.points[2], this.points[1]];
      this.finish();
    } else this.preview(p);
  }
  preview(p: Point) {
    if (!this.shapeId) return;
    let points = [...this.points, p];
    if (this.kind() === "curve" && this.points.length === 2)
      points = [this.points[0], p, this.points[1]];
    this.editor.updateShape<DraftingShape>({
      id: this.shapeId,
      type: "drafting",
      props: { points },
    });
  }
  override onPointerMove() {
    if (this.points.length) this.preview(this.point());
  }
  override onPointerUp() {
    if (this.kind() === "circle" && this.points.length) {
      const p = this.point();
      if (Math.hypot(p.x - this.points[0].x, p.y - this.points[0].y) > 1) {
        this.points = [this.points[0], p];
        this.finish();
      }
    }
  }
  override onDoubleClick() {
    if (["wall", "polyline"].includes(this.kind())) this.finish();
  }
  override onKeyDown(info: { key: string }) {
    if (info.key === "Escape") this.cancel();
    if (info.key === "Enter" && ["wall", "polyline"].includes(this.kind()))
      this.finish();
    if (info.key === "Backspace") {
      this.points.pop();
      if (!this.points.length) this.cancel();
      else this.preview(this.point());
    }
  }
  finish() {
    if (!this.shapeId || this.points.length < 2) return;
    const id = this.shapeId;
    this.editor.updateShape<DraftingShape>({
      id,
      type: "drafting",
      props: { points: this.points },
    });
    this.shapeId = undefined;
    this.points = [];
    this.drawingParent = undefined;
    this.editor.setCurrentTool("select");
    this.editor.select(id);
  }
  cancel() {
    if (this.shapeId && this.history) this.editor.bailToMark(this.history);
    this.shapeId = undefined;
    this.points = [];
    this.editor.setCurrentTool("select");
  }
  override onCancel() {
    this.cancel();
  }
  override onExit() {
    if (this.shapeId && this.history) this.editor.bailToMark(this.history);
    this.shapeId = undefined;
    this.drawingParent = undefined;
    this.points = [];
  }
  placeOpening() {
    const p = this.editor.inputs.getCurrentPagePoint();
    const walls = this.editor
      .getCurrentPageShapesSorted()
      .filter(
        (s): s is DraftingShape =>
          s.type === "drafting" &&
          s.props.kind === "wall" &&
          !this.editor.isShapeOrAncestorLocked(s) &&
          !this.editor.isShapeHidden(s),
      );
    const hit = walls
      .map((wall) => ({
        wall,
        hit: nearestWallSegment(
          wall.props.points,
          this.editor.getPointInShapeSpace(wall, p),
        ),
      }))
      .filter(
        (x) =>
          x.hit &&
          x.hit.distance <=
            Math.max(
              x.wall.props.thickness / 2,
              12 / this.editor.getZoomLevel(),
            ),
      )
      .sort((a, b) => a.hit.distance - b.hit.distance)[0];
    if (!hit) {
      notify("Click a wall to place the opening.");
      return;
    }
    const { wall } = hit;
    const style = resolvedDraftingStyle(
      this.editor,
      this.kind(),
      sourceMeters(this.editor, scaleSource(this.editor, wall)),
    );
    if (hit.hit.length < 2) {
      notify("Use a longer wall segment.");
      return;
    }
    const placement = openingPlacement(
      wall.props.points,
      hit.hit.segment,
      hit.hit.t,
      style.width,
    );
    const id = createShapeId();
    this.editor.markHistoryStoppingPoint(`Place ${this.kind()}`);
    this.editor.createShape<DraftingShape>({
      id,
      type: "drafting",
      parentId: wall.id,
      x: placement.x,
      y: placement.y,
      rotation: placement.rotation,
      opacity: 1,
      props: {
        ...style,
        points: [
          { x: 0, y: 0 },
          { x: 1, y: 0 },
        ],
        width: placement.width,
        thickness: wall.props.thickness,
        segment: hit.hit.segment,
        position: hit.hit.t,
      },
    });
    this.editor.setCurrentTool("select");
    this.editor.select(id);
  }
}
export class CircleTool extends DraftingTool {
  static override id = "circle";
}
export class CurveTool extends DraftingTool {
  static override id = "curve";
}
export class WallTool extends DraftingTool {
  static override id = "wall";
}
export class DoorTool extends DraftingTool {
  static override id = "door";
}
export class WindowTool extends DraftingTool {
  static override id = "window";
}
export class LeaderTool extends DraftingTool {
  static override id = "leader";
}
export class MoveTool extends StateNode {
  static override id = "move";
  start?: Point;
  history?: string;
  override onEnter() {
    this.editor.setCursor({ type: "move", rotation: 0 });
  }
  override onPointerDown() {
    if (this.editor.getIsReadonly()) return;
    if (!this.editor.getSelectedShapeIds().length) {
      const hit = this.editor.getShapeAtPoint(
        this.editor.inputs.getCurrentPagePoint(),
        { hitInside: true },
      );
      if (hit && !this.editor.isShapeOrAncestorLocked(hit))
        this.editor.select(hit.id);
    }
    if (!this.editor.getSelectedShapeIds().length) return;
    this.history = this.editor.markHistoryStoppingPoint("Move selection", { hold: true });
    this.start = this.editor.inputs.getCurrentPagePoint().clone();
  }
  override onPointerMove() {
    if (!this.start) return;
    const p = this.editor.inputs.getCurrentPagePoint();
    this.editor.nudgeShapes(this.editor.getSelectedShapeIds(), {
      x: p.x - this.start.x,
      y: p.y - this.start.y,
    });
    this.start = { x: p.x, y: p.y };
  }
  override onPointerUp() {
    this.start = undefined;
    this.editor.setCurrentTool("select");
  }
  override onCancel() {
    if (this.history && this.start) this.editor.bailToMark(this.history);
    this.start = undefined;
    this.editor.setCurrentTool("select");
  }
}
export const draftingTools = [
  DraftingTool,
  CircleTool,
  CurveTool,
  WallTool,
  DoorTool,
  WindowTool,
  LeaderTool,
  MoveTool,
];
